import { useEffect } from 'react';
import { type Message } from '@doxyme/extensions-common';
import { AlertBannerCapability, type Specs } from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { useAppDispatch } from '../../useReduxToolkit';
import { addAlert, dismissAlert } from '../../../features/alerts/alerts.slice';
import { AlertData } from '../../../features/alerts/types';

type AddPayload = Specs.AlertBanner.AddPayload;
type DismissPayload = Specs.AlertBanner.DismissPayload;

const getCallbackFn = (
  sendMessage: (message: Message) => void,
  callbackMessage?: AddPayload['callback'],
  dismissMessage?: AddPayload['dismissCallback'],
): AlertData['callback'] => {
  if (callbackMessage === undefined && dismissMessage === undefined) {
    return undefined;
  }

  return (state) => {
    if (state === 'actionClicked' && callbackMessage) {
      sendMessage(callbackMessage);
    } else if (state === 'dismissed' && dismissMessage) {
      sendMessage(dismissMessage);
    }
  };
};

export function useExtensionsAlertBanner(): void {
  const dispatch = useAppDispatch();
  const alertBanner = useAddCapability(
    bridge,
    () => new AlertBannerCapability(),
  );

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];
    unsubscribes.push(
      alertBanner.onAdd(
        (
          { callback, dismissCallback, ...payload }: AddPayload,
          sendMessage,
        ): void =>
          void dispatch(
            addAlert({
              ...payload,
              bannerMode: payload.bannerMode as AlertData['bannerMode'],
              callback: getCallbackFn(sendMessage, callback, dismissCallback),
            }),
          ),
      ),
      alertBanner.onDismiss(
        (payload: DismissPayload): void => void dispatch(dismissAlert(payload)),
      ),
    );

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
    };
  }, []);
}
